import { getApiBase } from '@services/apiBase';
const API_BASE = getApiBase();
import { getTurnstileToken, refreshTurnstileToken } from '@services/turnstile';
import { getAdClickIds } from '@services/adAttribution';
import { getConsentSnapshot } from '@services/consent';
import { getBrowserId } from '@services/browser';
import { clientUserAgentHeaders } from '@services/clientInfo';
import { getAccessToken, isLoggedIn, setAccessToken, isTokenExpired, shouldRefreshToken, EXPIRATION_SKEW_SECONDS } from '@services/token';
import { UploadLimitError } from './UploadLimitError';
import { DownloadLimitError } from './DownloadLimitError';
import { UploadSizeMismatchError } from './UploadSizeMismatchError';
import type { UploadLimitDetails } from './UploadLimitError';
import { apiRequest } from './apiRequest';
import { assertSafeStorageId } from '@services/identifiers';

export type LoginResult = { accessToken?: string; error?: string };

export type RegisterPayload = {
  email: string;
  firstName: string;
  lastName: string;
  companyName?: string | null;
  password: string;
  confirmPassword: string;
  language?: string;
  returnUrl?: string;
};

export async function login(emailOrUsername: string, password: string, rememberMe: boolean, language?: string): Promise<LoginResult> {
  try {
    const data = await apiRequest<any>('/auth/login', {
      method: 'POST',
      body: { emailOrUsername, password, rememberMe, language },
      auth: false
    });
    
    if (data?.accessToken) {
      setAccessToken(data.accessToken);
      return { accessToken: data.accessToken };
    }
    return { error: 'unexpected_response' };
  } catch (err: any) {
    return { error: err.message || 'login_failed' };
  }
}

export async function forgotPassword(email: string, language?: string): Promise<{ ok: boolean; error?: string }> {
  try {
    await apiRequest('/auth/password/forgot', {
      method: 'POST',
      body: { email, language },
      resp: 'void',
      errorMap: { 400: 'request_failed' }
    });
    return { ok: true };
  } catch (err: any) {
    if (err.message === 'request_failed') return { ok: false, error: 'request_failed' };
    return { ok: true };
  }
}

export async function resetPassword(email: string, token: string, newPassword: string): Promise<{ ok: boolean; error?: string }> {
  try {
    await apiRequest('/auth/password/reset', {
      method: 'POST',
      body: { email, token, newPassword },
      resp: 'void'
    });
    return { ok: true };
  } catch (err: any) {
    return { ok: false, error: err.message || 'reset_failed' };
  }
}

export async function refreshAccessToken(force = false): Promise<boolean> {
  const token = getAccessToken();
  if (!token) return false;

  if (!force && !shouldRefreshToken(token)) return true;

  if (isTokenExpired(token, EXPIRATION_SKEW_SECONDS)) {
    setAccessToken(null, { expired: true });
    return false;
  }

  try {
    const data = await apiRequest<{ accessToken?: string }>('/auth/token/refresh', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      auth: false,
      errorMap: { 401: 'session_expired' }
    });

    if (typeof data?.accessToken === 'string') {
      setAccessToken(data.accessToken);
      return true;
    }

    setAccessToken(null, { expired: true });
    return false;
  } catch (err: any) {
    if (err.message === 'session_expired') {
      setAccessToken(null, { expired: true });
    }
    return false;
  }
}

export async function bootstrapAuth(): Promise<void> {
  const token = getAccessToken();
  if (!token) return;

  if (isTokenExpired(token, EXPIRATION_SKEW_SECONDS)) {
    setAccessToken(null, { expired: true });
    return;
  }

  if (shouldRefreshToken(token)) {
    await refreshAccessToken(true);
  }
}

export async function register(payload: RegisterPayload): Promise<{ ok: boolean; error?: string }> {
  try {
    const response = await apiRequest('/auth/register', {
      method: 'POST',
      body: payload,
      resp: 'void'
    });
    return { ok: true };
  } catch (err: any) {
    return { ok: false, error: err.message || 'registration_failed' };
  }
}

export async function completePendingOAuth(pendingToken: string): Promise<{ accessToken?: string; returnUrl?: string; error?: string }> {
  try {
    const response = await apiRequest<any>('/auth/oauth/complete', {
      method: 'POST',
      body: { pendingToken },
      auth: false
    });

    if (typeof response?.accessToken === 'string') {
      return {
        accessToken: response.accessToken,
        returnUrl: typeof response?.returnUrl === 'string' && response.returnUrl ? response.returnUrl : undefined
      };
    }
    return { error: 'unexpected_response' };
  } catch (err: any) {
    return { error: err.message || 'oauth_complete_failed' };
  }
}


export class MetadataPasswordError extends Error {
  public readonly code: 'PasswordRequired' | 'InvalidPassword';

  constructor(code: 'PasswordRequired' | 'InvalidPassword', message?: string) {
    super(message || code);
    this.name = 'MetadataPasswordError';
    this.code = code;
  }
}

export class DownloadFileNotFoundError extends Error {
  public readonly code: 'FileNotFound';

  constructor(message?: string) {
    super(message || 'FileNotFound');
    this.name = 'DownloadFileNotFoundError';
    this.code = 'FileNotFound';
  }
}

export type { UploadLimitDetails };

export { UploadLimitError, DownloadLimitError, UploadSizeMismatchError };



export async function getAnonymousUsage(): Promise<AnonymousUsageSummary> {
  return apiRequest<AnonymousUsageSummary>('/uploads/anonymous/usage', {
    headers: { 'X-Browser-Id': getBrowserId() }
  });
}

export async function getRegions(): Promise<string[]> {
  const payload = await apiRequest<any>('/regions', {
    errorMap: { 500: 'Failed to load regions' }
  });
  
  if (Array.isArray(payload)) return payload as string[];
  if (Array.isArray(payload?.regions)) return payload.regions as string[];
  return [];
}

export async function getCurrentRegion(): Promise<{ currentRegion?: string; userCountry?: string }> {
  const payload = await apiRequest<any>('/regions/current');

  // Handle legacy response format
  if (typeof payload === 'string') return { currentRegion: payload };
  if (typeof payload?.region === 'string') return { currentRegion: payload.region };
  
  // Handle new response format
  return {
    currentRegion: payload?.currentRegion,
    userCountry: payload?.userCountry,
  };
}

interface CreateUploadDto {
  uploadId: string;
  metadataCiphertext: string;
  fileIds: string[];
  expiresAtIso?: string;
  browserId?: string; // For anonymous upload tracking
  totalUploadSize?: number; // Total upload size in bytes
  retentionPeriodCode?: string;
  hasPassword?: boolean;
  saltedPasswordHash?: string;
  maxDownloads?: number;
  notifyByEmail?: boolean;
  brandingProfileId?: string;
  uploadAdminPassword?: string;
}

interface UploadPreflightDto {
  totalUploadSize: number;
  fileCount: number;
  browserId?: string;
}

export interface AnonymousUsageSummary {
  currentUsageBytes: number;
  limitBytes: number;
  remainingBytes: number;
  percentageUsed: number;
  windowDays: number;
  generatedAtUtc?: string;
  uploads?: UploadUsageEntryDto[];
}

export async function createUploadSession(dto: CreateUploadDto, apiBaseOverride?: string): Promise<void> {
  // Add browser ID for anonymous upload tracking
  if (!isLoggedIn()) {
    dto.browserId = getBrowserId();
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...clientUserAgentHeaders() };
  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const ts = getTurnstileToken(); if (ts) headers['cf-turnstile-response'] = ts;
  const base = (apiBaseOverride && apiBaseOverride.trim().length > 0) ? apiBaseOverride : API_BASE;
  const response = await fetch(`${base}/uploads`, {
    method: 'POST',
    headers,
    body: JSON.stringify(dto)
  });

  if (!response.ok) {
    if (response.status === 400 || response.status === 402) {
      const errorText = await response.text();

      try {
        const error = JSON.parse(errorText) as {
          error?: string;
          message?: string;
          failureReason?: string;
          currentUsageBytes?: number;
          currentUsageMB?: number;
          limitBytes?: number;
          limitMB?: number;
          remainingBytes?: number;
          remainingMB?: number;
          percentageUsed?: number;
          requestedBytes?: number;
          windowDays?: number;
          details?: string;
        };

        const errorCode = typeof error.error === 'string' ? error.error : 'UploadLimitExceeded';
        const message = typeof error.message === 'string'
          ? error.message
          : typeof error.details === 'string'
            ? error.details
            : errorText || response.statusText;

        const details: UploadLimitDetails = {
          failureReason: error.failureReason,
          currentUsageBytes: error.currentUsageBytes,
          currentUsageMB: error.currentUsageMB,
          limitBytes: error.limitBytes,
          limitMB: error.limitMB,
          remainingBytes: error.remainingBytes,
          remainingMB: error.remainingMB,
          percentageUsed: error.percentageUsed,
          requestedBytes: error.requestedBytes,
          windowDays: error.windowDays,
        };

        throw new UploadLimitError(errorCode, message, details);
      } catch (parseError) {
        if (parseError instanceof UploadLimitError) {
          throw parseError;
        }

        if (parseError instanceof SyntaxError) {
          console.error('[API] Failed to parse upload limit error response as JSON:', parseError);
        }

        throw new UploadLimitError('UploadLimitExceeded', errorText || response.statusText);
      }
    }

    throw new Error(`Failed to create upload session: ${response.statusText}`);
  }
}

export async function requestUploadId(
  retentionPeriodCode: string,
  brandingProfileId?: string,
  apiBaseOverride?: string,
  regionKey?: string,
  totalUploadSize?: number
): Promise<string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const ts = getTurnstileToken(); if (ts) headers['cf-turnstile-response'] = ts;
  const base = (apiBaseOverride && apiBaseOverride.trim().length > 0) ? apiBaseOverride : API_BASE;
  const body: Record<string, string | number> = { retentionPeriodCode };

  if (brandingProfileId) {
    body.brandingProfileId = brandingProfileId;
  }
  if (regionKey) {
    body.regionKey = regionKey;
  }
  if (totalUploadSize !== undefined) {
    body.totalUploadSize = totalUploadSize;
  }

  // Add browserId for anonymous users to ensure consistent tracking
  if (!isLoggedIn()) {
    body.browserId = getBrowserId();
  }
  const transientStatuses = new Set([502, 503, 504]);
  const retryDelaysMs = [750, 2250];
  let response: Response | undefined;
  let lastNetworkError: unknown;

  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
    try {
      response = await fetch(`${base}/uploads/new`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });
      lastNetworkError = undefined;
    } catch (error) {
      lastNetworkError = error;
      response = undefined;
    }

    const shouldRetry = response
      ? transientStatuses.has(response.status)
      : lastNetworkError !== undefined;
    if (!shouldRetry || attempt === retryDelaysMs.length) {
      break;
    }

    await new Promise((resolve) => setTimeout(resolve, retryDelaysMs[attempt]));
  }

  if (!response) {
    throw lastNetworkError instanceof Error
      ? lastNetworkError
      : new Error('Failed to obtain upload id: network error');
  }

  if (!response.ok) {
    if (response.status === 402) {
      const error = await response.json() as { error: string; details: string };
      throw new UploadLimitError(error.error, error.details);
    }
    throw new Error(`Failed to obtain upload id: ${response.statusText}`);
  }
  
  const data = await response.json();
  if (!data?.uploadId) {
    throw new Error('Backend did not return an uploadId');
  }
  return data.uploadId as string;
}

export async function getFileUploadUrl(uploadId: string, fileId: string, apiBaseOverride?: string): Promise<string> {
  assertSafeStorageId(uploadId, 'uploadId');
  assertSafeStorageId(fileId, 'fileId');

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const ts = getTurnstileToken(); if (ts) headers['cf-turnstile-response'] = ts;

  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (!isLoggedIn()) {
    headers['X-Browser-Id'] = getBrowserId();
  }

  const base = (apiBaseOverride && apiBaseOverride.trim().length > 0) ? apiBaseOverride : API_BASE;
  const response = await fetch(
    `${base}/uploads/${encodeURIComponent(uploadId)}/files/${encodeURIComponent(fileId)}/upload-url`,
    {
      method: 'POST',
      headers,
      body: '{}'
    }
  );

  const payload = await response.json().catch(() => ({} as { uploadUrl?: string; lifetimeSeconds?: number }));

  if (!response.ok) {
    const message = typeof (payload as any).message === 'string'
      ? (payload as any).message
      : response.statusText;
    throw new Error(`Failed to get upload URL: ${message}`);
  }

  if (!payload.uploadUrl) {
    throw new Error('Backend did not provide an upload URL');
  }

  return payload.uploadUrl;
}

/**
 * Delivers an end-of-transfer signal reliably. These beacons decide whether a transfer counts as
 * completed or failed on the stats dashboard, and by nature they often fire on connections that just
 * proved flaky - a single plain fetch produced false "failures" in the field (download succeeded,
 * beacon lost). keepalive lets the request outlive a closing page, spaced retries ride out transient
 * drops, and sendBeacon is the last resort (no headers, so the event loses member attribution, but a
 * completion still gets recorded). Never throws.
 */
async function postEndSignal(url: string, headers: Record<string, string>): Promise<void> {
  for (const delayMs of [0, 2000, 6000]) {
    if (delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    try {
      const response = await fetch(url, { method: 'POST', headers, keepalive: true });
      if (response.ok) {
        return;
      }
    } catch {
      // Transient network failure - retry after the next delay.
    }
  }
  try {
    if ('sendBeacon' in navigator) {
      navigator.sendBeacon(url, '');
    }
  } catch {
    // Monitoring signal only - give up silently.
  }
}

/**
 * Signals that an upload finished successfully - every file's bytes reached storage. Records an
 * UploadEnded activity event that drives the 30-day success/failure trend (the bytes stream to the
 * edge worker / blob storage and never touch the backend, so only the client knows it actually
 * worked). Best-effort and never throws: a missed call simply shows up as a failed attempt on the
 * dashboard, so it must not disrupt the user's flow.
 */
export async function markUploadEnded(uploadId: string, apiBaseOverride?: string): Promise<void> {
  try {
    assertSafeStorageId(uploadId, 'uploadId');
    const headers: Record<string, string> = { ...clientUserAgentHeaders() };
    const token = getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const ts = getTurnstileToken(); if (ts) headers['cf-turnstile-response'] = ts;
    const base = (apiBaseOverride && apiBaseOverride.trim().length > 0) ? apiBaseOverride : API_BASE;
    await postEndSignal(`${base}/uploads/${uploadId}/uploaded`, headers);
  } catch {
    // Monitoring signal only - swallow everything so reporting never breaks an upload.
  }
}

/**
 * Signals that a download finished successfully - the file was fully fetched and decrypted. Records
 * a DownloadEnded activity event for the 30-day success/failure trend. Best-effort and never throws;
 * a missed call shows up as a failed attempt on the dashboard.
 */
export async function markDownloadEnded(uploadId: string, fileId: string): Promise<void> {
  try {
    assertSafeStorageId(uploadId, 'uploadId');
    assertSafeStorageId(fileId, 'fileId');
    const headers: Record<string, string> = { ...clientUserAgentHeaders() };
    const token = getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const ts = getTurnstileToken(); if (ts) headers['cf-turnstile-response'] = ts;
    await postEndSignal(`${API_BASE}/uploads/${uploadId}/files/${fileId}/downloaded`, headers);
  } catch {
    // Monitoring signal only.
  }
}

export async function preflightUpload(dto: UploadPreflightDto, apiBaseOverride?: string): Promise<void> {
  const payload: UploadPreflightDto = { ...dto };
  if (!isLoggedIn()) {
    payload.browserId = getBrowserId();
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const ts = getTurnstileToken(); if (ts) headers['cf-turnstile-response'] = ts;

  const base = (apiBaseOverride && apiBaseOverride.trim().length > 0) ? apiBaseOverride : API_BASE;
  const response = await fetch(`${base}/uploads/preflight`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    if (response.status === 400 || response.status === 402) {
      const errorText = await response.text();

      try {
        const error = JSON.parse(errorText) as UploadLimitDetails & { error?: string; message?: string };
        throw new UploadLimitError(error.error ?? 'UploadLimitExceeded', error.message ?? errorText, error);
      } catch (parseError) {
        if (parseError instanceof UploadLimitError) {
          throw parseError;
        }

        throw new UploadLimitError('UploadLimitExceeded', errorText || response.statusText);
      }
    }

    throw new Error(`Failed to preflight upload: ${response.statusText}`);
  }
}

export async function uploadEncryptedFile(
  uploadId: string,
  fileId: string,
  stream: ReadableStream<Uint8Array>
): Promise<void> {
  assertSafeStorageId(uploadId, 'uploadId');
  assertSafeStorageId(fileId, 'fileId');

  // Create a backup branch so we can retry non-streaming if needed
  const [primary, backup] = stream.tee();

  const headers: Record<string, string> = { 'Content-Type': 'application/octet-stream' };
  const ts = getTurnstileToken(); if (ts) headers['cf-turnstile-response'] = ts;
  const opts: RequestInit & { duplex: 'half' } = {
    method: 'PUT',
    headers,
    // Required by Chromium when sending a ReadableStream as body
    duplex: 'half',
    body: primary
  };

  let response: Response | undefined;
  try {
    const base = API_BASE;
    response = await fetch(
      `${base}/uploads/${uploadId}/files/${fileId}`,
      opts
    );
  } catch (err) {
    // Fallback for environments where streaming uploads are not supported
    // or fail due to transport constraints: buffer to Blob and retry.
    const buffered = await new Response(backup).blob();
    response = await fetch(
      `${API_BASE}/uploads/${uploadId}/files/${fileId}`,
      {
        method: 'PUT',
        headers,
        body: buffered
      }
    );
  }

  if (!response.ok) {
    throw new Error(`Failed to upload file ${fileId}: ${response.statusText}`);
  }
}

export async function uploadEncryptedChunk(
  uploadId: string,
  fileId: string,
  chunkIndex: number,
  chunk: Blob | Uint8Array
): Promise<void> {
  assertSafeStorageId(uploadId, 'uploadId');
  assertSafeStorageId(fileId, 'fileId');

  const headers: Record<string, string> = { 'Content-Type': 'application/octet-stream' };
  const ts = getTurnstileToken(); if (ts) headers['cf-turnstile-response'] = ts;
  const response = await fetch(
    `${API_BASE}/uploads/${uploadId}/files/${fileId}/chunks/${chunkIndex}`,
    {
      method: 'PUT',
      headers,
      body: chunk instanceof Uint8Array ? new Blob([chunk.buffer as ArrayBuffer], { type: 'application/octet-stream' }) : chunk
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to upload chunk ${chunkIndex}: ${response.statusText}`);
  }
}

export async function completeEncryptedFile(
  uploadId: string,
  fileId: string,
  totalChunks: number
): Promise<void> {
  assertSafeStorageId(uploadId, 'uploadId');
  assertSafeStorageId(fileId, 'fileId');

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const ts = getTurnstileToken(); if (ts) headers['cf-turnstile-response'] = ts;
  const response = await fetch(
    `${API_BASE}/uploads/${uploadId}/files/${fileId}/complete`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ totalChunks })
    }
  );

  if (!response.ok) {
    if (response.status === 400) {
      const error = await response.json() as { error: string; details: string };
      if (error.error === 'upload_size_mismatch') {
        throw new UploadSizeMismatchError(error.error, error.details);
      }
    }
    throw new Error(`Failed to finalize file ${fileId}: ${response.statusText}`);
  }
}

export function abortUpload(uploadId: string, apiBaseOverride?: string): void {
  assertSafeStorageId(uploadId, 'uploadId');
  const base = (apiBaseOverride && apiBaseOverride.trim().length > 0) ? apiBaseOverride : API_BASE;
  const url = `${base}/uploads/${uploadId}/abort`;
  try {
    if ('sendBeacon' in navigator) {
      // Use a CORS-safelisted content type by sending a plain string body
      // so the beacon is allowed cross-origin without preflight.
      (navigator as any).sendBeacon(url, '');
      return;
    }
  } catch {
    // fall back to fetch keepalive
  }

  try {
    // Attempt a keepalive fetch so it can survive tab close
    const headers: Record<string, string> = { 'Content-Type': 'text/plain;charset=UTF-8' };
    const ts = getTurnstileToken(); if (ts) headers['cf-turnstile-response'] = ts;
    void fetch(url, {
      method: 'POST',
      mode: 'cors',
      keepalive: true,
      headers,
      body: ''
    });
  } catch {
    // ignore
  }
}

export async function fetchEncryptedMetadata(uploadId: string, saltedPasswordHash?: string): Promise<string> {
  assertSafeStorageId(uploadId, 'uploadId');
  const headers: Record<string, string> = { ...clientUserAgentHeaders() };
  const ts = getTurnstileToken(); if (ts) headers['cf-turnstile-response'] = ts;

  // Add browser ID for anonymous metadata tracking
  if (!isLoggedIn()) {
    headers['X-Browser-Id'] = getBrowserId();
  }

  if (typeof saltedPasswordHash === 'string' && saltedPasswordHash.length > 0) {
    headers['X-Download-Password-Hash'] = saltedPasswordHash;
  }

  const response = await fetch(`${API_BASE}/uploads/${uploadId}/metadata`, { headers });

  if (response.status === 401) {
    let code: 'PasswordRequired' | 'InvalidPassword' = 'PasswordRequired';
    let message: string | undefined;

    try {
      const payload = await response.json().catch(() => ({} as Record<string, unknown>));
      const errorCode = typeof (payload as any).error === 'string' ? (payload as any).error : undefined;
      if (errorCode === 'InvalidPassword') {
        code = 'InvalidPassword';
      } else if (errorCode === 'PasswordRequired') {
        code = 'PasswordRequired';
      }
      if (typeof (payload as any).message === 'string') {
        message = (payload as any).message as string;
      }
    } catch {
      // ignore JSON parse errors and fall back to defaults
    }

    throw new MetadataPasswordError(code, message);
  }

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(text || `Unable to fetch metadata: ${response.statusText}`);
  }

  return await response.text();
}

const DOWNLOAD_SAS_MAX_RETRIES = 5;
const DOWNLOAD_SAS_INITIAL_RETRY_DELAY_MS = 1000;

/**
 * Wraps a SAS/worker download URL in a ReadableStream that transparently resumes after a dropped
 * connection by re-requesting from the last delivered byte with an HTTP Range header. The downstream
 * decryptor consumes one continuous byte stream, so resuming is invisible to it (decryption is
 * offset-independent - it just reads a framed byte sequence). We reuse the SAME URL, never re-hitting
 * the backend, so a resume never double-counts a download or double-consumes the per-file limit; the
 * SAS lifetime is sized generously server-side to cover slow connections plus retries. The Range
 * header is only sent on resume (delivered > 0), so the first request stays a simple, preflight-free
 * GET exactly as before. The consecutive-failure budget resets on every byte of progress, so a link
 * that drops repeatedly but keeps advancing can still finish; only a truly stalled connection gives up.
 */
function createResumableSasStream(
  sasUrl: string,
  signal: AbortSignal | undefined,
  maxConsecutiveFailures: number,
  initialDelayMs: number
): ReadableStream<Uint8Array> {
  let delivered = 0;            // encrypted bytes already handed downstream
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  let skipRemaining = 0;        // bytes to discard if a server ignored our Range (200 instead of 206)
  let failures = 0;             // consecutive failures since the last byte of progress
  let delayMs = initialDelayMs;

  const open = async (): Promise<'ok' | 'complete'> => {
    const headers: Record<string, string> = {};
    if (delivered > 0) headers['Range'] = `bytes=${delivered}-`;
    const resp = await fetch(sasUrl, { method: 'GET', signal, mode: 'cors', headers });
    if (resp.status === 416) {
      // Range Not Satisfiable - we already hold the whole file.
      return 'complete';
    }
    if (!resp.ok || !resp.body) {
      throw new Error(`Failed to fetch encrypted file: ${resp.status}`);
    }
    // Server ignored the Range and returned the full body from 0: discard what we already delivered.
    skipRemaining = delivered > 0 && resp.status !== 206 ? delivered : 0;
    reader = resp.body.getReader();
    return 'ok';
  };

  return new ReadableStream<Uint8Array>({
    async pull(controller) {
      while (true) {
        if (signal?.aborted) {
          controller.error(new Error('Download aborted'));
          return;
        }
        try {
          if (!reader) {
            const state = await open();
            if (state === 'complete') {
              controller.close();
              return;
            }
          }
          const { done, value } = await reader!.read();
          if (done) {
            controller.close();
            return;
          }
          if (!value || value.byteLength === 0) {
            continue;
          }
          // Received bytes prove the connection recovered - reset the failure budget.
          failures = 0;
          delayMs = initialDelayMs;

          let chunk = value;
          if (skipRemaining > 0) {
            if (chunk.byteLength <= skipRemaining) {
              skipRemaining -= chunk.byteLength;
              continue;
            }
            chunk = chunk.subarray(skipRemaining);
            skipRemaining = 0;
          }
          delivered += chunk.byteLength;
          controller.enqueue(chunk);
          return;
        } catch (err) {
          if (signal?.aborted) {
            controller.error(err instanceof Error ? err : new Error('Download aborted'));
            return;
          }
          failures += 1;
          if (failures >= maxConsecutiveFailures) {
            controller.error(err instanceof Error ? err : new Error('Failed to fetch encrypted file'));
            return;
          }
          reader = null; // force a fresh ranged request on the next loop iteration
          if (delayMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
            delayMs *= 2;
          }
        }
      }
    },
    cancel(reason) {
      try {
        void reader?.cancel(reason);
      } catch {
        // ignore
      }
    }
  });
}

export async function fetchEncryptedFileStream(
  uploadId: string,
  fileId: string,
  signal?: AbortSignal,
  saltedPasswordHash?: string
): Promise<ReadableStream<Uint8Array>> {
  assertSafeStorageId(uploadId, 'uploadId');
  assertSafeStorageId(fileId, 'fileId');
  const headers: Record<string, string> = { ...clientUserAgentHeaders() };
  const ts = getTurnstileToken();
  if (ts) headers['cf-turnstile-response'] = ts;

  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (!isLoggedIn()) {
    headers['X-Browser-Id'] = getBrowserId();
  }

  if (typeof saltedPasswordHash === 'string' && saltedPasswordHash.length > 0) {
    headers['X-Download-Password-Hash'] = saltedPasswordHash;
  }

  let backendResponse = await fetch(`${API_BASE}/uploads/${uploadId}/files/${fileId}`, {
    method: 'GET',
    headers,
    signal
  });

  // During multi-file downloads the cached Turnstile token can expire or get consumed mid-burst,
  // which the backend rejects as 400 "Invalid Turnstile token" (observed in the field: the user just
  // sees the file fail). Re-run the challenge for a fresh token and retry once before giving up.
  if (backendResponse.status === 400) {
    const bodyText = await backendResponse.clone().text().catch(() => '');
    if (bodyText.includes('Invalid Turnstile token')) {
      const freshToken = await refreshTurnstileToken();
      if (freshToken) {
        headers['cf-turnstile-response'] = freshToken;
        backendResponse = await fetch(`${API_BASE}/uploads/${uploadId}/files/${fileId}`, {
          method: 'GET',
          headers,
          signal
        });
      }
    }
  }

  if (backendResponse.status === 403) {
    const error = await backendResponse.json().catch(() => ({} as Record<string, unknown>));
    const details: UploadLimitDetails = {
      failureReason: typeof error.failureReason === 'string' ? error.failureReason : undefined,
      currentUsageBytes: typeof error.currentUsageBytes === 'number' ? error.currentUsageBytes : undefined,
      currentUsageMB: typeof error.currentUsageMB === 'number' ? error.currentUsageMB : undefined,
      limitBytes: typeof error.limitBytes === 'number' ? error.limitBytes : undefined,
      limitMB: typeof error.limitMB === 'number' ? error.limitMB : undefined,
      remainingBytes: typeof error.remainingBytes === 'number' ? error.remainingBytes : undefined,
      remainingMB: typeof error.remainingMB === 'number' ? error.remainingMB : undefined,
      percentageUsed: typeof error.percentageUsed === 'number' ? error.percentageUsed : undefined,
      requestedBytes: typeof error.requestedBytes === 'number' ? error.requestedBytes : undefined,
      windowDays: typeof error.windowDays === 'number' ? error.windowDays : undefined
    };

    throw new DownloadLimitError(
      typeof error.error === 'string' ? error.error : 'DownloadLimitExceeded',
      typeof error.details === 'string'
        ? error.details
        : typeof error.message === 'string'
          ? error.message
          : 'Download limit exceeded',
      details,
      typeof error.limitType === 'string' ? error.limitType : undefined
    );
  }

  if (backendResponse.status === 404) {
    let message: string | undefined;
    let errorCode: string | undefined;

    try {
      const payload = await backendResponse.json().catch(() => ({} as Record<string, unknown>));
      if (typeof (payload as any).message === 'string') {
        message = (payload as any).message as string;
      }
      if (typeof (payload as any).error === 'string') {
        errorCode = (payload as any).error as string;
      }
    } catch {
      // ignore JSON parse errors and fall back to defaults
    }

    throw new DownloadFileNotFoundError(message || errorCode || 'File not found');
  }

  if (!backendResponse.ok) {
    const text = await backendResponse.text().catch(() => backendResponse.statusText);
    throw new Error(`Failed to prepare download: ${backendResponse.status} ${text}`);
  }

  const downloadInfo = await backendResponse.json().catch(() => null) as
    | {
        sasUrl?: string;
        expiresOn?: string;
        lifetimeSeconds?: number;
        fileSizeBytes?: number;
      }
    | null;

  if (!downloadInfo?.sasUrl) {
    throw new Error('Backend did not provide a download URL');
  }

  // Stream the encrypted bytes with transparent resume: a dropped connection re-requests from the
  // last delivered byte via Range instead of restarting the whole file (the previous loop only
  // retried the initial fetch and would return to byte 0). Reuses the same SAS URL so the backend
  // limit isn't double-consumed.
  return createResumableSasStream(
    downloadInfo.sasUrl,
    signal,
    DOWNLOAD_SAS_MAX_RETRIES,
    DOWNLOAD_SAS_INITIAL_RETRY_DELAY_MS
  );
}

export type BlogPost = {
  id: number;
  title: string;
  slug: string;
  language: string;
  contentHtml: string;
  isPublished: boolean;
  authorId: number | null;
  createdAtUtc: string;
  updatedAtUtc?: string | null;
  publishedAtUtc?: string | null;
};

export type BlogListResponse = {
  blogs: BlogPost[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
};

export type BlogDetailResponse = {
  blog: BlogPost;
};

export type BlogCreateRequest = {
  title: string;
  contentHtml: string;
  language?: string;
};

export type BlogUpdateRequest = {
  id: number;
  title: string;
  contentHtml: string;
  language?: string;
};

export type BlogImportPayload = {
  language?: string;
  blogs: {
    title: string;
    contentHtml: string;
    language?: string;
  }[];
};

export type BlogImportResponse = {
  imported: number;
  skipped: number;
  blogs: BlogPost[];
};

export async function fetchBlogs(
  language?: string,
  options?: { search?: string; page?: number; pageSize?: number }
): Promise<BlogListResponse> {
  const params = new URLSearchParams();
  if (language && language.trim().length > 0) {
    params.set('lang', language.trim());
  }
  if (options?.search && options.search.trim().length > 0) {
    params.set('q', options.search.trim());
  }
  if (typeof options?.page === 'number') {
    params.set('page', String(options.page));
  }
  if (typeof options?.pageSize === 'number') {
    params.set('pageSize', String(options.pageSize));
  }

  const data = await apiRequest<BlogListResponse>(`/blogs?${params.toString()}`, {
    errorMap: { 404: 'Failed to load blogs' }
  });

  const blogs = Array.isArray(data.blogs) ? data.blogs : [];
  const page = typeof data.page === 'number' ? data.page : options?.page ?? 1;
  const pageSize = typeof data.pageSize === 'number' ? data.pageSize : options?.pageSize ?? 10;
  const totalCount = typeof data.totalCount === 'number' ? data.totalCount : blogs.length;
  const totalPages = typeof data.totalPages === 'number'
    ? data.totalPages
    : Math.max(1, Math.ceil(totalCount / Math.max(1, pageSize)));

  return {
    blogs,
    page,
    pageSize,
    totalCount,
    totalPages
  };
}

export async function fetchBlog(slug: string): Promise<BlogDetailResponse> {
  const data = await apiRequest<BlogDetailResponse>(`/blogs/${encodeURIComponent(slug)}` , {
    errorMap: { 404: 'blog_not_found' }
  });

  if (!data.blog) throw new Error('blog_not_found');
  return data;
}

export async function createBlog(request: BlogCreateRequest): Promise<BlogPost> {
  return apiRequest<BlogPost>('/blogs', {
    method: 'POST',
    body: request,
    requireAuth: true,
    errorMap: { 400: 'blog_create_failed' }
  });
}

export async function updateBlog(request: BlogUpdateRequest): Promise<BlogPost> {
  return apiRequest<BlogPost>(`/blogs/${request.id}`, {
    method: 'PUT',
    body: { title: request.title, contentHtml: request.contentHtml, language: request.language },
    requireAuth: true,
    errorMap: { 404: 'blog_not_found', 400: 'blog_update_failed' }
  });
}

export async function setBlogPublished(id: number, isPublished: boolean): Promise<void> {
  await apiRequest(`/blogs/${id}/${isPublished ? 'publish' : 'unpublish'}`, {
    method: 'POST',
    requireAuth: true,
    resp: 'void',
    errorMap: { 404: 'blog_not_found' }
  });
}

export async function deleteBlog(id: number): Promise<void> {
  await apiRequest(`/blogs/${id}`, {
    method: 'DELETE',
    requireAuth: true,
    resp: 'void',
    errorMap: { 404: 'blog_not_found' }
  });
}

export async function exportBlogs(): Promise<BlogPost[]> {
  const data = await apiRequest<BlogPost[]>('/blogs/export', {
    method: 'GET',
    requireAuth: true
  });
  return Array.isArray(data) ? data : [];
}

export async function importBlogs(payload: BlogImportPayload): Promise<BlogImportResponse> {
  return apiRequest<BlogImportResponse>('/blogs/import', {
    method: 'POST',
    body: payload,
    requireAuth: true
  });
}

export interface UploadFileInfo {
  id: string;
  name: string;
  size: number;
  contentType: string;
  downloadsCount?: number;
  maxDownloadsPerFile?: number;
}

export interface UploadInformation {
  uploadId: string;
  shareableLink?: string;
  createdAt: string;
  files: UploadFileInfo[];
  hostingRegion?: string;
  maxDownloads?: number | null;
  isPasswordProtected: boolean;
  emailNotification: boolean;
  expiryDate?: string;
  uploadAdminPassword?: string;
  brandingProfileId?: string;
  downloadsCount?: number;
}

export interface UpdateUploadRequest {
  uploadId: string;
  uploadAdminPassword: string;
  enableEmailNotification?: boolean;
  maxDownloads?: number;
  passwordHash?: string;
  brandingProfileId?: string;
  expiryDate?: string;
}

export interface DeleteUploadRequest {
  uploadId: string;
  uploadAdminPassword: string;
}

export interface GetUploadDetailsRequest {
  uploadId: string;
  uploadAdminPassword: string;
}

export interface FileDownloadInfo {
  fileId: string;
  downloadCount: number;
  maxDownloads?: number;
}

export interface UpdateFileDownloadInfoParams {
  uploadId: string;
  fileId: string;
  uploadAdminPassword: string;
  maxDownloads?: number;
}

export async function getUserEncryptionKey(browserId?: string): Promise<{ encryptionKey: string }> {
  const url = `/user/encryption-key${browserId ? `?browserId=${browserId}` : ''}`;
  return apiRequest<{ encryptionKey: string }>(url, {
    auth: true,
    requireAuth: !browserId,
    ignore404: true
  });
}

export async function getUploadDetails(uploadId: string, uploadAdminPassword: string): Promise<UploadInformation> {
  assertSafeStorageId(uploadId, 'uploadId');
  return apiRequest<UploadInformation>('/uploads/details', {
    method: 'POST',
    body: { UploadId: uploadId, UploadAdminPassword: uploadAdminPassword },
    errorMap: { 400: 'Failed to get upload details' }
  });
}

export async function updateUpload(request: UpdateUploadRequest): Promise<UploadInformation> {
  return apiRequest<UploadInformation>('/uploads', {
    method: 'PUT',
    body: {
      UploadId: request.uploadId,
      UploadAdminPassword: request.uploadAdminPassword,
      EnableEmailNotification: request.enableEmailNotification,
      MaxDownloads: request.maxDownloads,
      PasswordHash: request.passwordHash,
      BrandingProfileId: request.brandingProfileId,
      ExpiryDate: request.expiryDate
    },
    errorMap: { 400: 'Failed to update upload' }
  });
}

export async function deleteUpload(uploadId: string, uploadAdminPassword: string): Promise<void> {
  assertSafeStorageId(uploadId, 'uploadId');
  await apiRequest(`/uploads/${uploadId}`, {
    method: 'DELETE',
    body: { UploadId: uploadId, UploadAdminPassword: uploadAdminPassword },
    resp: 'void',
    errorMap: { 404: 'Upload not found' }
  });
}

export async function deleteFile(uploadId: string, fileId: string, uploadAdminPassword: string): Promise<void> {
  assertSafeStorageId(uploadId, 'uploadId');
  assertSafeStorageId(fileId, 'fileId');
  await apiRequest(`/uploads/${uploadId}/files/${fileId}`, {
    method: 'DELETE',
    body: { UploadId: uploadId, FileId: fileId, UploadAdminPassword: uploadAdminPassword },
    resp: 'void',
    errorMap: { 404: 'File not found' }
  });
}

export async function updateMetadata(uploadId: string, uploadAdminPassword: string, metadataCiphertext: string): Promise<void> {
  assertSafeStorageId(uploadId, 'uploadId');
  await apiRequest(`/uploads/${uploadId}/metadata`, {
    method: 'PUT',
    body: {
      UploadId: uploadId,
      UploadAdminPassword: uploadAdminPassword,
      MetadataCiphertext: metadataCiphertext
    },
    resp: 'void'
  });
}

export async function getFilesDownloadInfo(uploadId: string, uploadAdminPassword: string): Promise<FileDownloadInfo[]> {
  assertSafeStorageId(uploadId, 'uploadId');
  const data = await apiRequest<any[]>('/uploads/files-info', {
    method: 'POST',
    body: { UploadId: uploadId, UploadAdminPassword: uploadAdminPassword }
  });

  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((item: any) => ({
    fileId: item.fileId ?? item.FileId ?? '',
    downloadCount: item.downloadCount ?? item.DownloadCount ?? 0,
    maxDownloads: item.maxDownloads ?? item.MaxDownloads
  }));
}

export async function updateFileDownloadInfo(params: UpdateFileDownloadInfoParams): Promise<FileDownloadInfo> {
  const data = await apiRequest<any>('/uploads/files-info', {
    method: 'PUT',
    body: {
      UploadId: params.uploadId,
      FileId: params.fileId,
      UploadAdminPassword: params.uploadAdminPassword,
      MaxDownloads: typeof params.maxDownloads === 'number' ? params.maxDownloads : null
    }
  });

  return {
    fileId: data.fileId ?? data.FileId ?? params.fileId,
    downloadCount: data.downloadCount ?? data.DownloadCount ?? 0,
    maxDownloads: data.maxDownloads ?? data.MaxDownloads ?? params.maxDownloads
  };
}




// --- Usage ---

export interface UsageMetricDto {
  usedBytes: number;
  limitBytes: number;
  remainingBytes: number;
  percentage: number;
}

export interface UsageCountMetricDto {
  usedCount: number;
  limitCount: number;
  remainingCount: number;
  percentage: number;
}

export interface UsageSummaryDto {
  upload: UsageMetricDto;
  windowDays: number;
  generatedAtUtc: string;
}

export interface UploadUsageEntryDto {
  sizeBytes: number;
  uploadedAtUtc: string;
  countsUntilUtc: string;
  isCurrentUser: boolean;
}

export interface UserUsageSummaryDto {
  userId: number;
  usage: UsageSummaryDto;
  uploads: UploadUsageEntryDto[];
  isShared: boolean;
  capacitySeatCount: number;
  capacityPerSeatBytes: number;
  hasLicenseSeat: boolean;
  limitSource: string;
  ownedLicenseId?: number | null;
  seatId?: number | null;
  seatIndex?: number | null;
  seatDisplayName?: string | null;
}

export interface SeatUsageSummaryDto {
  seatId: number;
  seatIndex: number;
  assignedToUserId?: number | null;
  assignedToEmail?: string | null;
  assignedToDisplayName?: string | null;
  usage: UsageSummaryDto;
}

export interface LicenseUsageSummaryDto {
  licenseId: number;
  licenseType: string;
  usage: UsageSummaryDto;
}

export async function getUserUsage(): Promise<UserUsageSummaryDto> {
  return apiRequest<UserUsageSummaryDto>('/usage/user');
}

// --- Stats ---

export interface DailyUploadCount {
  date: string;
  // Successful uploads (client-signalled UploadEnded) split by actor, deliberate cancels
  // (UploadCancelled), and genuine failures (UploadStarted - UploadEnded - UploadCancelled, clamped
  // at 0). Sourced only from the ActivityEvent log.
  anonymousEnded: number;
  memberEnded: number;
  cancelled: number;
  failed: number;
}

export interface DailyDownloadCount {
  date: string;
  anonymousEnded: number;
  memberEnded: number;
  failed: number;
}

export interface DailyCount {
  date: string;
  count: number;
}

export interface TopUploader {
  email: string | null;
  country: string | null;
  uploadCount: number;
  lastUploadUtc: string;
  isLicensed: boolean;
}

export interface HeavyUser {
  email: string | null;
  country: string | null;
  uploadCount: number;
  azureBytes: number;
  cloudflareBytes: number;
  totalBytes: number;
  isLicensed: boolean;
}

export interface CountryBreakdown {
  country: string;
  total: number;
  windows: number;
  linux: number;
  iphone: number;
  mac: number;
  ipad: number;
  android: number;
}

export interface ApplicationStatsDto {
  generatedAtUtc: string;
  totalAnonymousUsers: number;
  totalMemberUsers: number;
  totalLicensedMembers: number;
  anonymousUsersByCountry: CountryBreakdown[];
  memberUsersByCountry: CountryBreakdown[];
  totalAnonymousUploads: number;
  totalMemberUploads: number;
  licensesByCountry: CountryBreakdown[];
  totalHostedBytes: number;
  azureHostedBytes: number;
  cloudflareHostedBytes: number;
  auditCount: number;
  dailyUploads?: DailyUploadCount[];
  dailyDownloads?: DailyDownloadCount[];
  dailyRegistrations?: DailyCount[];
  dailyActiveMembers?: DailyCount[];
  topUploaders?: TopUploader[];
  heavyUsers?: HeavyUser[];
}

export async function getStats(): Promise<ApplicationStatsDto> {
  return apiRequest<ApplicationStatsDto>('/stats', {
    requireAuth: true,
    errorMap: { default: 'stats_failed' }
  });
}

// --- Contact ---

export interface ContactPayload {
  subject: string;
  email: string;
  company?: string;
  phone?: string;
  message: string;
}

export async function submitContactMessage(payload: ContactPayload): Promise<void> {
  await apiRequest('/contact', {
    method: 'POST',
    body: payload,
    resp: 'void'
  });
}

// --- Admin ---

export interface AdminBlockMaliciousUserResult {
  success: boolean;
  uploadsDiscovered: number;
  uploadsDeleted: number;
  memberUsersDiscovered: number;
  memberUsersLocked: number;
  anonymousUsersDiscovered: number;
  ipAddressHashesDiscovered: number;
  ipAddressHashesBlocked: number;
  emailsNotified: number;
  uploadIds: string[];
  emails: string[];
}

export interface AdminBlockMaliciousUserRequest {
  email?: string;
  shareLinkOrUploadId?: string;
  sendEmails?: boolean;
  language?: string;
}

export interface AdminDeleteUploadRequest {
  shareLinkOrUploadId?: string;
}

export interface AdminDeleteUploadResult {
  success: boolean;
  uploadId?: string;
  storageDeleteAttempted: boolean;
  storageDeleted: boolean;
  memberUploadInformationRowsDeleted: number;
  anonymousUploadInformationRowsDeleted: number;
}

export async function adminBlockMaliciousUser(request: AdminBlockMaliciousUserRequest): Promise<AdminBlockMaliciousUserResult> {
  return apiRequest<AdminBlockMaliciousUserResult>('/admin/block', {
    method: 'POST',
    body: request,
    requireAuth: true,
    errorMap: { default: 'admin_block_failed' }
  });
}

export async function adminDeleteUpload(request: AdminDeleteUploadRequest): Promise<AdminDeleteUploadResult> {
  return apiRequest<AdminDeleteUploadResult>('/admin/delete-upload', {
    method: 'POST',
    body: request,
    requireAuth: true,
    errorMap: { default: 'admin_delete_failed' }
  });
}

// --- Billing ---

export async function createSetupIntent(): Promise<{ clientSecret: string }> {
  return apiRequest<{ clientSecret: string }>('/billing-methods/setup-intent', {
    method: 'POST'
  });
}

// --- Warmup ---

export function warmupFunctions(): void {
  try {
    fetch(`${API_BASE}/ping`, { method: 'GET', keepalive: true }).catch(() => {});
  } catch { /* ignore */ }
}

// --- License / Payment / Address ---

export enum LicenseType {
  Pro = 'Pro',
  Education = 'Education',
  Ultimate = 'Ultimate'
}

export enum Currency {
  USD = 'USD',
  EUR = 'EUR',
  CHF = 'CHF'
}

export enum BillingCadence {
  Monthly = 'Monthly',
  Yearly = 'Yearly'
}

export interface LicenseDto {
  id: number;
  seatCount: number;
  type: LicenseType;
  validUntil: string;
  billingCadence: BillingCadence;
  owner: string;
  autoRenewal: boolean;
  stripeBalanceCents: number;
  stripeSubscriptionId?: string | null;
  maxUploadPerMonthInGb: number;
  maxDownloadPerMonthInGb: number;
  maxUploadsPerMonth: number;
  maxDownloadsPerMonth: number;
}

export interface LicenseSeatDto {
  id: number;
  index: number;
  assignedToUserId?: number;
  assignedToEmail?: string;
  assignedToDisplayName?: string;
  lastAssignedOn?: string | null;
}

export interface LicenseSeatInvitationDto {
  id: number;
  email: string;
  createdAtUtc?: string | null;
  acceptedAtUtc?: string | null;
}

export enum LicenseDomainVerificationStatus {
  NotRequested = 0,
  Pending = 1,
  Verified = 2
}

export interface CustomDomainDto {
  id: number;
  domain: string;
  domainVerificationStatus: LicenseDomainVerificationStatus;
  verificationCode?: string | null;
  requestedAtUtc?: string | null;
  verifiedAtUtc?: string | null;
  dnsRecordName?: string | null;
  dnsRecordValue?: string | null;
}

export interface AssignLicenseSeatRequest {
  seatId: number;
  email: string;
}

export interface InviteLicenseSeatRequest {
  email: string;
}

export interface AcceptLicenseSeatRequest {
  code: string;
  password?: string;
}

export interface UserLicenseResponse {
  hasLicense: boolean;
  hadLicenseInPast: boolean;
  license?: LicenseDto;
  licenseUsage?: LicenseUsageSummaryDto | null;
}

export interface BrandingThemeFontsDto {
  body: string;
  heading: string;
  mono: string;
}

export interface BrandingThemeColorsDto {
  bg: string;
  surface: string;
  surfaceMuted: string;
  text: string;
  textMuted: string;
  border: string;
  primary: string;
  primaryHover: string;
  onPrimary: string;
  danger: string;
  success: string;
}

export interface BrandingThemeVariablesDto {
  fonts: BrandingThemeFontsDto;
  light: BrandingThemeColorsDto;
  dark: BrandingThemeColorsDto;
  preferredColorScheme?: 'light' | 'dark' | null;
}

export interface BrandingProfileDto {
  id: number;
  name: string;
  isMainProfile: boolean;
  theme?: BrandingThemeVariablesDto | null;
}

export interface BrandingLogoDto {
  data: string;
  mimeType: string;
}

export interface CreateBrandingProfileRequest {
  name?: string;
  isMainProfile?: boolean;
  theme?: BrandingThemeVariablesDto | null;
}

export interface UpdateBrandingProfileRequest {
  name?: string;
  isMainProfile?: boolean;
  theme?: BrandingThemeVariablesDto | null;
}

export enum SsoType {
  Saml = 0,
  Oidc = 1,
  EntraId = 2
}

export interface SamlServiceEndpointDto {
  binding: string;
  location: string;
  isDefault?: boolean;
}

export interface SsoConfigurationDto {
  id: number;
  organizationId: number;
  name: string;
  type: SsoType;
  enabled: boolean;
  samlEntityId?: string;
  samlMetadataUrl?: string; // UI-only field for loading metadata
  samlCertificate?: string;
  samlWantAuthnRequestsSigned: boolean;
  samlSingleSignOnServices: SamlServiceEndpointDto[];
  samlSingleLogoutServices?: SamlServiceEndpointDto[];
  samlArtifactResolutionService?: SamlServiceEndpointDto;
  oidcAuthority?: string;
  oidcMetadataUrl?: string; // UI-only field for loading metadata
  oidcAuthorizationEndpoint?: string;
  oidcTokenEndpoint?: string;
  oidcUserInfoEndpoint?: string;
  oidcClientId?: string;
  oidcClientSecret?: string;
  spCertificatePem?: string;
  spCertificateThumbprint?: string;
  spCertificateExpiresAtUtc?: string;
  spKeyAlgorithm?: string;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface CreateSsoConfigurationRequest {
  name: string;
  type: SsoType;
  enabled: boolean;
  samlEntityId?: string;
  samlCertificate?: string;
  samlWantAuthnRequestsSigned?: boolean;
  samlSingleSignOnServices?: SamlServiceEndpointDto[];
  oidcAuthority?: string;
  oidcAuthorizationEndpoint?: string;
  oidcTokenEndpoint?: string;
  oidcUserInfoEndpoint?: string;
  oidcClientId?: string;
  oidcClientSecret?: string;
}

export interface SsoSpMetadataDetailsDto {
  spEntityId?: string;
  assertionConsumerServiceUrl?: string;
  audience?: string;
  redirectUri?: string;
  loginInitiationUrl?: string;
  metadataDownloadUrl?: string;
  certificatePem?: string;
  certificateThumbprint?: string;
  certificateExpiresAtUtc?: string;
  keyAlgorithm?: string;
}

export interface SsoMetadataFileResult {
  fileName: string;
  contentType: string;
  content: string;
}

export interface UpdateSsoConfigurationRequest {
  name?: string;
  type?: SsoType;
  enabled?: boolean;
  samlEntityId?: string;
  samlCertificate?: string;
  samlWantAuthnRequestsSigned?: boolean;
  samlSingleSignOnServices?: SamlServiceEndpointDto[];
  oidcAuthority?: string;
  oidcAuthorizationEndpoint?: string;
  oidcTokenEndpoint?: string;
  oidcUserInfoEndpoint?: string;
  oidcClientId?: string;
  oidcClientSecret?: string;
}

export interface LicenseSubdomainDto {
  id: number;
  subdomain: string;
  brandingProfileId?: number | null;
  ssoConfigurationIds?: number[];
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface CreateSubdomainRequest {
  subdomain: string;
}

export interface UpdateSubdomainRequest {
  subdomain?: string;
  brandingProfileId?: number | null;
  ssoConfigurationIds?: number[];
}

export interface SubdomainAvailabilityResponse {
  available: boolean;
  reason?: string | null;
}

export interface SsoConfigurationPublic {
  id: number;
  name: string;
  type: SsoType;
}

export async function getPublicSsoConfigurations(subdomain: string): Promise<SsoConfigurationPublic[]> {
  return apiRequest<SsoConfigurationPublic[]>(`/subdomains/${encodeURIComponent(subdomain)}/sso-configurations`, {
    auth: false,
    errorMap: { default: 'Failed to load SSO configurations' }
  });
}

export async function getSubdomainBrandingProfile(subdomain: string): Promise<BrandingProfileDto | null> {
  return apiRequest<BrandingProfileDto | null>(`/subdomains/${encodeURIComponent(subdomain)}/branding-profile`, {
    auth: false,
    ignore404: true,
    errorMap: { default: 'Failed to load branding' }
  });
}

export async function getUserLicense(): Promise<UserLicenseResponse> {
  const data = await apiRequest<UserLicenseResponse | undefined>('/license');
  if (!data) return { hasLicense: false, hadLicenseInPast: false };
  return data;
}

export async function getLicenseSeats(): Promise<LicenseSeatDto[]> {
  return apiRequest<LicenseSeatDto[]>('/license/seats');
}

export async function getSeatInvitations(): Promise<LicenseSeatInvitationDto[]> {
  return apiRequest<LicenseSeatInvitationDto[]>('/license/seats/invitations');
}

export async function assignSeat(request: AssignLicenseSeatRequest): Promise<{ message: string }> {
  return apiRequest<{ message: string }>('/license/seats/assign', {
    method: 'POST',
    body: request
  });
}

export async function unassignSeat(seatId: number): Promise<{ message: string }> {
  return apiRequest<{ message: string }>(`/license/seats/${seatId}/unassign`, {
    method: 'POST'
  });
}

export async function assignOrganizationMemberSeat(memberId: number): Promise<{ message: string }> {
  return apiRequest<{ message: string }>(`/organization/members/${memberId}/seat/assign`, {
    method: 'POST'
  });
}

export async function unassignOrganizationMemberSeat(memberId: number): Promise<{ message: string }> {
  return apiRequest<{ message: string }>(`/organization/members/${memberId}/seat/unassign`, {
    method: 'POST'
  });
}

export async function removeOrganizationMember(memberId: number): Promise<void> {
  await apiRequest(`/organization/members/${memberId}`, {
    method: 'DELETE',
    resp: 'void'
  });
}

export async function inviteSeat(request: InviteLicenseSeatRequest): Promise<LicenseSeatInvitationDto> {
  return apiRequest<LicenseSeatInvitationDto>('/license/seats/invite', {
    method: 'POST',
    body: request
  });
}

export async function cancelLicense(): Promise<{ success: boolean }> {
  return apiRequest<{ success: boolean }>('/license/cancel', {
    method: 'POST'
  });
}

export async function cancelSeatInvitation(invitationId: number): Promise<{ message: string }> {
  return apiRequest<{ message: string }>(`/license/seats/invitations/${invitationId}/cancel`, {
    method: 'POST'
  });
}

export async function resendSeatInvitation(invitationId: number): Promise<LicenseSeatInvitationDto> {
  return apiRequest<LicenseSeatInvitationDto>(`/license/seats/invitations/${invitationId}/resend`, {
    method: 'POST'
  });
}

export async function acceptSeatInvitation(request: AcceptLicenseSeatRequest): Promise<void> {
  await apiRequest<void>('/license/seats/accept', {
    method: 'POST',
    body: request,
    resp: 'void'
  });
}

export async function getCustomDomains(): Promise<CustomDomainDto[]> {
  return apiRequest<CustomDomainDto[]>('/license/domains');
}

export async function requestCustomDomain(domain: string): Promise<CustomDomainDto> {
  return apiRequest<CustomDomainDto>('/license/domains/request', {
    method: 'POST',
    body: { domain }
  });
}

export async function verifyCustomDomain(domainId: number): Promise<CustomDomainDto> {
  return apiRequest<CustomDomainDto>(`/license/domains/${domainId}/verify`, {
    method: 'POST'
  });
}

export async function removeCustomDomain(domainId: number): Promise<void> {
  await apiRequest(`/license/domains/${domainId}`, {
    method: 'DELETE',
    resp: 'void'
  });
}

export async function getBrandingProfiles(): Promise<BrandingProfileDto[]> {
  return apiRequest<BrandingProfileDto[]>('/license/branding-profiles');
}

export async function getJoinedBrandingProfiles(): Promise<BrandingProfileDto[]> {
  return apiRequest<BrandingProfileDto[]>('/license/branding-profiles/joined');
}

export async function createBrandingProfile(request: CreateBrandingProfileRequest): Promise<BrandingProfileDto> {
  return apiRequest<BrandingProfileDto>('/license/branding-profiles', {
    method: 'POST',
    body: request
  });
}

export async function updateBrandingProfile(profileId: number, request: UpdateBrandingProfileRequest): Promise<BrandingProfileDto> {
  return apiRequest<BrandingProfileDto>(`/license/branding-profiles/${profileId}`, {
    method: 'PUT',
    body: request
  });
}

export async function deleteBrandingProfile(profileId: number): Promise<void> {
  await apiRequest(`/license/branding-profiles/${profileId}`, {
    method: 'DELETE',
    resp: 'void'
  });
}

export async function getSsoConfigurations(): Promise<SsoConfigurationDto[]> {
  const token = getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/license/sso-configurations`, { headers });
  if (!res.ok) throw new Error(`Failed to get SSO configurations: ${res.status}`);
  return res.json();
}

export async function createSsoConfiguration(request: CreateSsoConfigurationRequest): Promise<SsoConfigurationDto> {
  const token = getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/license/sso-configurations`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request)
  });
  if (!res.ok) throw new Error(`Failed to create SSO configuration: ${res.status}`);
  return res.json();
}

export async function updateSsoConfiguration(configId: number, request: UpdateSsoConfigurationRequest): Promise<SsoConfigurationDto> {
  const token = getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/license/sso-configurations/${configId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(request)
  });
  if (!res.ok) throw new Error(`Failed to update SSO configuration: ${res.status}`);
  return res.json();
}

export async function deleteSsoConfiguration(configId: number): Promise<void> {
  const token = getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/license/sso-configurations/${configId}`, {
    method: 'DELETE',
    headers
  });
  if (!res.ok) throw new Error(`Failed to delete SSO configuration: ${res.status}`);
}

export async function getSsoMetadata(configId: number): Promise<SsoSpMetadataDetailsDto> {
  const token = getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/license/sso-configurations/${configId}/metadata`, { headers });
  if (!res.ok) throw new Error(`Failed to get SSO metadata: ${res.status}`);
  return res.json();
}

export async function downloadSsoMetadataFile(configId: number): Promise<Response> {
  const token = getAccessToken();
  const headers: Record<string, string> = { };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  return fetch(`${API_BASE}/license/sso-configurations/${configId}/metadata/file`, { headers });
}

export async function fetchSsoMetadataFromUrl(configId: number, metadataUrl: string): Promise<string> {
  return apiRequest<string>(`/license/sso-configurations/${configId}/metadata/proxy`, {
    method: 'POST',
    body: { metadataUrl },
    requireAuth: true,
    resp: 'text'
  });
}

export interface StripeCheckoutResultDto {
  checkoutUrl: string;
}

export interface StripePortalResultDto {
  portalUrl: string;
}

export async function createSubscriptionCheckout(billingCadence: BillingCadence, seatCount: number): Promise<StripeCheckoutResultDto> {
  // Carry the current first-party decision through Stripe. The backend independently fails closed,
  // stores click ids only with an explicit ad-user-data grant, and always denies personalization.
  const consent = getConsentSnapshot();
  const adsMeasurementGranted = consent.hasDecision && consent.preferences.adsMeasurement;
  const adClickIds = getAdClickIds() ?? {};
  return apiRequest<StripeCheckoutResultDto>('/license/subscribe/checkout', {
    method: 'POST',
    body: {
      billingCadence,
      seatCount,
      adUserDataConsent: adsMeasurementGranted ? 'granted' : 'denied',
      adPersonalizationConsent: 'denied',
      consentPolicyVersion: consent.hasDecision ? consent.policyVersion : undefined,
      consentTimestampUtc: consent.hasDecision ? consent.savedAt : undefined,
      ...adClickIds,
    }
  });
}

export async function completeSubscriptionCheckout(sessionId: string): Promise<{ success: boolean }> {
  return apiRequest<{ success: boolean }>('/license/subscribe/complete', {
    method: 'POST',
    body: { sessionId }
  });
}

export async function createBillingPortalSession(): Promise<StripePortalResultDto> {
  return apiRequest<StripePortalResultDto>('/license/billing/portal', {
    method: 'POST'
  });
}

export async function createInvoicePortalSession(): Promise<StripePortalResultDto> {
  return apiRequest<StripePortalResultDto>('/license/billing/invoices', {
    method: 'POST'
  });
}

export async function createCancelSubscriptionPortal(): Promise<StripePortalResultDto> {
  return apiRequest<StripePortalResultDto>('/license/billing/cancel', {
    method: 'POST'
  });
}

export interface UpdateSeatsResultDto {
  portalUrl: string;
}

export async function updateSubscriptionSeats(seatCount: number): Promise<UpdateSeatsResultDto> {
  return apiRequest<UpdateSeatsResultDto>('/license/seats', {
    method: 'POST',
    body: { seatCount }
  });
}

export interface PreviewSeatsResultDto {
  amountDue: number;
  currency: string;
}

export async function previewSubscriptionSeats(seatCount: number): Promise<PreviewSeatsResultDto> {
  return apiRequest<PreviewSeatsResultDto>('/license/seats/preview', {
    method: 'POST',
    body: { seatCount }
  });
}

export async function syncSubscription(): Promise<{ synced: boolean }> {
  return apiRequest<{ synced: boolean }>('/license/sync', {
    method: 'POST'
  });
}

export async function updateLicense(autoRenewal: boolean): Promise<{ message: string }> {
  return apiRequest<{ message: string }>('/license', {
    method: 'PUT',
    body: { autoRenewal }
  });
}

export async function getSubdomains(): Promise<LicenseSubdomainDto[]> {
  return apiRequest<LicenseSubdomainDto[]>('/license/subdomains');
}

export async function createSubdomain(request: CreateSubdomainRequest): Promise<LicenseSubdomainDto> {
  return apiRequest<LicenseSubdomainDto>('/license/subdomains', {
    method: 'POST',
    body: request
  });
}

export async function updateSubdomain(id: number, request: UpdateSubdomainRequest): Promise<LicenseSubdomainDto> {
  return apiRequest<LicenseSubdomainDto>(`/license/subdomains/${id}`, {
    method: 'PUT',
    body: request
  });
}

export async function deleteSubdomain(id: number): Promise<void> {
  await apiRequest(`/license/subdomains/${id}`, {
    method: 'DELETE',
    resp: 'void'
  });
}

export async function checkSubdomainAvailability(subdomain: string): Promise<SubdomainAvailabilityResponse> {
  return apiRequest<SubdomainAvailabilityResponse>(`/license/subdomains/check/${encodeURIComponent(subdomain)}`);
}

export async function uploadBrandingLogo(profileId: number, variant: 'light' | 'dark', logo: { data: string; mimeType: string }): Promise<void> {
  await apiRequest(`/license/branding-profiles/${profileId}/logo/${variant}`, {
    method: 'PUT',
    body: logo,
    resp: 'void'
  });
}

export async function deleteBrandingLogo(profileId: number, variant: 'light' | 'dark'): Promise<void> {
  await apiRequest(`/license/branding-profiles/${profileId}/logo/${variant}`, {
    method: 'DELETE',
    resp: 'void'
  });
}

export interface OrganizationMemberDto {
  id: number;
  email: string;
  joinedAtUtc: string;
  isOwner: boolean;
  hasSeat: boolean;
}

export interface OrganizationMembersPageDto {
  ownerId: number;
  members: OrganizationMemberDto[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface JoinedOrganizationDto {
  id: number;
  ownerEmail: string;
  hasSeat: boolean;
}

export async function getOrganizationMembers(params?: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<OrganizationMembersPageDto> {
  const query = new URLSearchParams();
  const search = params?.search?.trim();
  if (search) {
    query.set('search', search);
  }
  if (params?.page) {
    query.set('page', String(params.page));
  }
  if (params?.pageSize) {
    query.set('pageSize', String(params.pageSize));
  }
  const suffix = query.toString();
  const path = suffix ? `/organization/members?${suffix}` : '/organization/members';
  return apiRequest<OrganizationMembersPageDto>(path);
}

export async function getJoinedOrganizations(): Promise<JoinedOrganizationDto[]> {
  return apiRequest<JoinedOrganizationDto[]>('/organization/joined');
}

export async function leaveJoinedOrganization(organizationId: number): Promise<void> {
  await apiRequest(`/organization/joined/${organizationId}/leave`, {
    method: 'POST',
    resp: 'void'
  });
}

export async function getStripePublishableKey(): Promise<string> {
  const data = await apiRequest<{ publishableKey: string }>('/config/stripe');
  return data.publishableKey;
}

// --- Multipart Uploads ---

export interface MultipartCreateResponse {
  uploadId?: string;
}

export interface MultipartUploadPartResponse {
  etag?: string;
  partNumber?: number;
}

export async function createWorkerMultipartUpload(
  workerBaseUrl: string,
  uploadId: string,
  fileId: string
): Promise<string> {
  const createUrl = `?action=mpu-create&uploadId=${encodeURIComponent(uploadId)}&fileId=${encodeURIComponent(fileId)}`;
  
  const created = await apiRequest<MultipartCreateResponse>(createUrl, {
    method: 'POST',
    apiBase: workerBaseUrl,
    mode: 'cors',
    auth: false
  });

  if (!created.uploadId) {
    throw new Error('Worker did not return a valid multipart upload id');
  }
  return created.uploadId;
}

export async function uploadWorkerMultipartPart(
  workerBaseUrl: string,
  uploadId: string,
  fileId: string,
  r2UploadId: string,
  partNumber: number,
  blob: Blob
): Promise<string> {
  const uploadPartUrl = `?action=mpu-uploadpart&uploadId=${encodeURIComponent(uploadId)}&fileId=${encodeURIComponent(fileId)}&r2UploadId=${encodeURIComponent(r2UploadId)}&partNumber=${partNumber}`;
  
  const uploaded = await apiRequest<MultipartUploadPartResponse>(uploadPartUrl, {
    method: 'PUT',
    apiBase: workerBaseUrl,
    body: blob,
    mode: 'cors',
    auth: false
  });

  if (!uploaded.etag) {
    throw new Error(`Worker did not return ETag for part ${partNumber}`);
  }
  return uploaded.etag;
}

export async function completeWorkerMultipartUpload(
  workerBaseUrl: string,
  uploadId: string,
  fileId: string,
  r2UploadId: string,
  parts: { etag: string; partNumber: number }[]
): Promise<void> {
  const completeUrl = `?action=mpu-complete&uploadId=${encodeURIComponent(uploadId)}&fileId=${encodeURIComponent(fileId)}&r2UploadId=${encodeURIComponent(r2UploadId)}`;
  
  await apiRequest(completeUrl, {
    method: 'POST',
    apiBase: workerBaseUrl,
    body: { parts },
    mode: 'cors',
    auth: false,
    resp: 'void'
  });
}

export async function uploadAzureBlock(url: string, blob: Blob): Promise<void> {
  // For direct Azure uploads, we use the full URL as apiBase and empty path
  await apiRequest('', {
    method: 'PUT',
    apiBase: url,
    headers: {
      'Content-Type': 'application/octet-stream'
    },
    body: blob,
    mode: 'cors',
    auth: false,
    resp: 'void',
    errorMap: { default: 'Azure upload block failed' }
  });
}

export async function commitAzureBlockList(url: string, blockListXml: string): Promise<void> {
  await apiRequest('', {
    method: 'PUT',
    apiBase: url,
    headers: {
      'Content-Type': 'application/xml'
    },
    body: blockListXml,
    mode: 'cors',
    auth: false,
    resp: 'void',
    errorMap: { default: 'Failed to finalize Azure upload' }
  });
}
